import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const types = [
  { value: "privat", label: "Privatperson" },
  { value: "student", label: "Student (MIUN)" },
  { value: "juridisk", label: "Företag / förening" },
];

export const MembershipModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", member_type: "privat", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setStatus("idle");
      setForm({ name: "", email: "", phone: "", member_type: "privat", message: "" });
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="membership-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-[#0f2419]/70 backdrop-blur-sm" onClick={close} />
          <motion.div
            className="relative w-full max-w-lg rounded-3xl bg-[#F7F7F4] border border-[#D1D8D3] p-8 sm:p-10 max-h-[92vh] overflow-y-auto"
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              data-testid="membership-close-btn"
              onClick={close}
              className="absolute top-5 right-5 text-[#4A6D56] hover:text-[#1A3B2A] transition-colors"
            >
              <X />
            </button>

            {status === "done" ? (
              <div className="text-center py-8" data-testid="membership-success">
                <span className="inline-grid place-items-center h-16 w-16 rounded-full bg-[#1A3B2A] text-[#F7F7F4] mb-6">
                  <Check size={30} />
                </span>
                <h3 className="font-display text-3xl text-[#1A3B2A]">Tack för din anmälan!</h3>
                <p className="text-[#1B1F1C]/70 mt-3">
                  Vi hör av oss till dig så snart som möjligt med nästa steg.
                </p>
                <button
                  onClick={close}
                  className="mt-8 inline-flex rounded-full bg-[#1A3B2A] text-[#F7F7F4] px-7 py-3 text-sm font-bold uppercase tracking-widest hover:brightness-125 active:scale-95 transition-[filter,transform] duration-300"
                >
                  Stäng
                </button>
              </div>
            ) : (
              <>
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#4A6D56]">Bli medlem</span>
                <h3 className="font-display text-3xl sm:text-4xl text-[#1A3B2A] mt-3 mb-6">
                  Anmäl ditt intresse
                </h3>
                <form onSubmit={submit} className="flex flex-col gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#1B1F1C]/70">Namn</label>
                    <input
                      required
                      data-testid="membership-name"
                      value={form.name}
                      onChange={set("name")}
                      className="mt-1 w-full rounded-xl border border-[#D1D8D3] bg-white px-4 py-3 outline-none focus:border-[#1A3B2A] transition-colors"
                      placeholder="För- och efternamn"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#1B1F1C]/70">E-post</label>
                      <input
                        required
                        type="email"
                        data-testid="membership-email"
                        value={form.email}
                        onChange={set("email")}
                        className="mt-1 w-full rounded-xl border border-[#D1D8D3] bg-white px-4 py-3 outline-none focus:border-[#1A3B2A] transition-colors"
                        placeholder="namn@exempel.se"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#1B1F1C]/70">Telefon</label>
                      <input
                        data-testid="membership-phone"
                        value={form.phone}
                        onChange={set("phone")}
                        className="mt-1 w-full rounded-xl border border-[#D1D8D3] bg-white px-4 py-3 outline-none focus:border-[#1A3B2A] transition-colors"
                        placeholder="070-123 45 67"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1B1F1C]/70">Jag vill bli medlem som</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {types.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          data-testid={`membership-type-${t.value}`}
                          onClick={() => setForm((f) => ({ ...f, member_type: t.value }))}
                          className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors duration-200 ${
                            form.member_type === t.value
                              ? "bg-[#1A3B2A] text-[#F7F7F4] border-[#1A3B2A]"
                              : "bg-white text-[#1B1F1C]/70 border-[#D1D8D3] hover:border-[#1A3B2A]"
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1B1F1C]/70">Meddelande (valfritt)</label>
                    <textarea
                      data-testid="membership-message"
                      value={form.message}
                      onChange={set("message")}
                      rows={3}
                      className="mt-1 w-full rounded-xl border border-[#D1D8D3] bg-white px-4 py-3 outline-none focus:border-[#1A3B2A] transition-colors resize-none"
                      placeholder="Berätta gärna lite om dig själv"
                    />
                  </div>

                  {status === "error" && (
                    <p className="text-sm text-[#c0392b]" data-testid="membership-error">
                      Något gick fel. Försök igen eller mejla oss direkt.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="membership-submit"
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#1A3B2A] text-[#F7F7F4] px-7 py-4 text-sm font-bold uppercase tracking-widest hover:brightness-125 active:scale-95 transition-[filter,transform] duration-300 disabled:opacity-60"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Skickar…
                      </>
                    ) : (
                      <>
                        Skicka anmälan <Send size={16} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
